import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useCreateAsset } from '../../hooks/useAssets';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ASSET_TYPE_LABELS, type AssetType, type AssetForm } from '../../types';

const schema = z.object({
  code: z.string().min(1).max(20).toUpperCase(),
  name: z.string().min(2).max(255),
  type: z.string().min(1),
  sector: z.string().optional(),
  quantity: z.coerce.number().min(0),
  averagePrice: z.coerce.number().min(0),
  broker: z.string().optional(),
  firstPurchaseDate: z.string().min(1),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  onClose: () => void;
}

export function CreateAssetModal({ onClose }: Props) {
  const { mutate: createAsset, isPending } = useCreateAsset();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { firstPurchaseDate: new Date().toISOString().split('T')[0] },
  });

  const onSubmit = (data: FormData) => {
    createAsset(data as unknown as AssetForm, {
      onSuccess: () => onClose(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface-800 rounded-2xl border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <h2 className="text-lg font-semibold text-white">Novo Ativo</h2>
          <button onClick={onClose} className="btn-ghost p-2 rounded-lg">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Código *</label>
              <input {...register('code')} className="input" placeholder="PETR4" />
              {errors.code && <p className="form-error">{errors.code.message}</p>}
            </div>
            <div>
              <label className="label">Tipo *</label>
              <select {...register('type')} className="input">
                <option value="">Selecione...</option>
                {(Object.keys(ASSET_TYPE_LABELS) as AssetType[]).map((type) => (
                  <option key={type} value={type}>
                    {ASSET_TYPE_LABELS[type]}
                  </option>
                ))}
              </select>
              {errors.type && <p className="form-error">{errors.type.message}</p>}
            </div>
          </div>

          <div>
            <label className="label">Nome *</label>
            <input {...register('name')} className="input" placeholder="Petrobras PN" />
            {errors.name && <p className="form-error">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Quantidade *</label>
              <input
                type="number"
                step="any"
                {...register('quantity')}
                className="input"
                placeholder="100"
              />
              {errors.quantity && <p className="form-error">{errors.quantity.message}</p>}
            </div>
            <div>
              <label className="label">Preço Médio (R$) *</label>
              <input
                type="number"
                step="any"
                {...register('averagePrice')}
                className="input"
                placeholder="28.50"
              />
              {errors.averagePrice && (
                <p className="form-error">{errors.averagePrice.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Setor</label>
              <input
                {...register('sector')}
                className="input"
                placeholder="Energia"
              />
            </div>
            <div>
              <label className="label">Corretora</label>
              <input
                {...register('broker')}
                className="input"
                placeholder="XP Investimentos"
              />
            </div>
          </div>

          <div>
            <label className="label">Data Primeira Compra *</label>
            <input
              type="date"
              {...register('firstPurchaseDate')}
              className="input"
            />
            {errors.firstPurchaseDate && (
              <p className="form-error">{errors.firstPurchaseDate.message}</p>
            )}
          </div>

          <div>
            <label className="label">Observações</label>
            <textarea
              {...register('notes')}
              className="input resize-none h-20"
              placeholder="Notas sobre este ativo..."
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Cancelar
            </button>
            <button type="submit" disabled={isPending} className="btn-primary flex-1">
              {isPending ? <LoadingSpinner size="sm" /> : 'Cadastrar Ativo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
